import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  SegmentedControl,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPlug, IconTrash, IconEdit, IconPlugConnected } from '@tabler/icons-react'
import {
  buildWorkspaceMcpSseConfig,
  buildWorkspaceMcpStdioConfig,
  normalizeWorkspaceMcpSlugInput,
  parseWorkspaceMcpSseHeadersJson,
} from '@owlbert/core'
import { useOwlbertWebAuth } from '../../auth/OwlbertWebAuthContext'

type WorkspaceMcpSourceDto = {
  id: string
  name: string
  slug: string
  transport_type: 'stdio' | 'sse'
  config: Record<string, unknown>
  created_at: number
  updated_at: number
}

export const WebMcpServersSettingsPanel: React.FC = () => {
  const auth = useOwlbertWebAuth()
  const workspaceId = auth.workspaceId
  const client = auth.bundle?.client

  const [list, setList] = useState<WorkspaceMcpSourceDto[]>([])
  const [count, setCount] = useState(0)
  const [max, setMax] = useState(25)
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<WorkspaceMcpSourceDto | null>(null)
  const [transportKind, setTransportKind] = useState<'stdio' | 'sse'>('stdio')
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [command, setCommand] = useState('')
  const [argsJson, setArgsJson] = useState('[]')
  const [sseUrl, setSseUrl] = useState('')
  const [headersJson, setHeadersJson] = useState('{}')
  const [testBusy, setTestBusy] = useState<string | 'modal' | null>(null)

  const reload = useCallback(async () => {
    if (!client || !workspaceId) {
      setList([])
      setCount(0)
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const [sources, lim] = await Promise.all([
        client.owlbertHosted.workspaceMcp.list.query({ workspaceId }),
        client.owlbertHosted.workspaceMcp.limits.query({ workspaceId }),
      ])
      setList(sources as WorkspaceMcpSourceDto[])
      setCount(lim.count)
      setMax(lim.max)
    } catch (e: unknown) {
      notifications.show({
        title: 'Failed to load MCP servers',
        message: e instanceof Error ? e.message : 'Unknown error',
        color: 'red',
      })
    } finally {
      setLoading(false)
    }
  }, [client, workspaceId])

  useEffect(() => {
    void reload()
  }, [reload])

  const openCreate = () => {
    setEditing(null)
    setTransportKind('stdio')
    setName('')
    setSlug('')
    setCommand('')
    setArgsJson('[]')
    setSseUrl('')
    setHeadersJson('{}')
    setModalOpen(true)
  }

  const openEdit = (row: WorkspaceMcpSourceDto) => {
    setEditing(row)
    const tt = row.transport_type === 'sse' ? 'sse' : 'stdio'
    setTransportKind(tt)
    setName(row.name)
    setSlug(row.slug)
    if (tt === 'stdio') {
      const cmd = typeof row.config.command === 'string' ? row.config.command : ''
      setCommand(cmd)
      const args = Array.isArray(row.config.args)
        ? row.config.args.filter((x): x is string => typeof x === 'string')
        : []
      setArgsJson(JSON.stringify(args.length ? args : []))
      setSseUrl('')
      setHeadersJson('{}')
    } else {
      setCommand('')
      setArgsJson('[]')
      setSseUrl(typeof row.config.url === 'string' ? row.config.url : '')
      const h = row.config.headers
      if (h != null && typeof h === 'object' && !Array.isArray(h)) {
        setHeadersJson(JSON.stringify(h, null, 2))
      } else {
        setHeadersJson('{}')
      }
    }
    setModalOpen(true)
  }

  const saveModal = async () => {
    if (!client || !workspaceId) return
    let transport_type: 'stdio' | 'sse' = transportKind
    let config: Record<string, unknown>
    if (transportKind === 'stdio') {
      const built = buildWorkspaceMcpStdioConfig(command, argsJson)
      if (!built.ok) {
        notifications.show({ title: 'Command required', message: built.message, color: 'yellow' })
        return
      }
      config = built.config
    } else {
      const built = buildWorkspaceMcpSseConfig(sseUrl, headersJson)
      if (!built.ok) {
        notifications.show({
          title: built.message.startsWith('Headers') ? 'Invalid headers JSON' : 'URL required',
          message: built.message,
          color: 'yellow',
        })
        return
      }
      config = built.config
    }

    try {
      if (editing) {
        await client.owlbertHosted.workspaceMcp.update.mutate({
          workspaceId,
          id: editing.id,
          name: name.trim() || slug.trim(),
          slug: normalizeWorkspaceMcpSlugInput(name, slug),
          transport_type,
          config,
        })
        notifications.show({ title: 'Updated', message: editing.name, color: 'green' })
      } else {
        await client.owlbertHosted.workspaceMcp.create.mutate({
          workspaceId,
          name: name.trim() || slug.trim() || 'MCP server',
          slug: normalizeWorkspaceMcpSlugInput(name, slug),
          transport_type,
          config,
        })
        notifications.show({ title: 'Added', message: name.trim() || slug.trim() || 'MCP server', color: 'green' })
      }
      setModalOpen(false)
      await reload()
    } catch (e: unknown) {
      notifications.show({
        title: 'Save failed',
        message: e instanceof Error ? e.message : 'Unknown error',
        color: 'red',
      })
    }
  }

  const testSseConnection = async (url: string, headers: Record<string, string> | undefined, busyKey: string | 'modal') => {
    if (!client) return
    setTestBusy(busyKey)
    try {
      const res = await client.owlbertHosted.workspaceMcp.testSseConnection.mutate({
        url,
        ...(headers && Object.keys(headers).length > 0 ? { headers } : {}),
      })
      notifications.show({
        title: res.ok ? 'MCP connection OK' : 'MCP connection failed',
        message: res.message,
        color: res.ok ? 'green' : 'red',
      })
    } catch (e: unknown) {
      notifications.show({
        title: 'MCP connection test error',
        message: e instanceof Error ? e.message : 'Unknown error',
        color: 'red',
      })
    } finally {
      setTestBusy(null)
    }
  }

  const testDraftSse = async () => {
    const url = sseUrl.trim()
    if (!url || (!url.startsWith('http://') && !url.startsWith('https://'))) {
      notifications.show({
        title: 'URL required',
        message: 'Enter the MCP SSE endpoint URL before testing.',
        color: 'yellow',
      })
      return
    }
    const headers = parseWorkspaceMcpSseHeadersJson(headersJson)
    if (headers === null) {
      notifications.show({
        title: 'Invalid headers JSON',
        message: 'Headers must be a JSON object with string values.',
        color: 'yellow',
      })
      return
    }
    const h = Object.keys(headers).length > 0 ? headers : undefined
    await testSseConnection(url, h, 'modal')
  }

  const testSavedSseRow = async (row: WorkspaceMcpSourceDto) => {
    if (row.transport_type !== 'sse') return
    const url = typeof row.config.url === 'string' ? row.config.url.trim() : ''
    if (!url) {
      notifications.show({ title: 'Missing URL', message: 'This MCP entry has no URL.', color: 'yellow' })
      return
    }
    const raw = row.config.headers
    const headers =
      raw != null && typeof raw === 'object' && !Array.isArray(raw)
        ? (Object.fromEntries(
            Object.entries(raw as Record<string, unknown>).filter((e): e is [string, string] => typeof e[1] === 'string'),
          ) as Record<string, string>)
        : undefined
    const h = headers && Object.keys(headers).length > 0 ? headers : undefined
    await testSseConnection(url, h, row.id)
  }

  const remove = async (row: WorkspaceMcpSourceDto) => {
    if (!client || !workspaceId) return
    if (!window.confirm(`Remove MCP server "${row.name}"?`)) return
    try {
      await client.owlbertHosted.workspaceMcp.delete.mutate({ workspaceId, id: row.id })
      notifications.show({ title: 'Removed', message: row.name, color: 'green' })
      await reload()
    } catch (e: unknown) {
      notifications.show({
        title: 'Delete failed',
        message: e instanceof Error ? e.message : 'Unknown error',
        color: 'red',
      })
    }
  }

  const targetLabel = (row: WorkspaceMcpSourceDto): string => {
    if (row.transport_type === 'sse') {
      const u = typeof row.config.url === 'string' ? row.config.url : ''
      return u || '—'
    }
    return typeof row.config.command === 'string' ? row.config.command : '—'
  }

  if (!auth.sessionReady) {
    return (
      <Text size="sm" c="dimmed" maw={720}>
        Sign in to manage MCP servers for your selected workspace.
      </Text>
    )
  }

  if (!workspaceId) {
    return (
      <Alert color="yellow" title="Pick a workspace">
        Choose a workspace in General before configuring MCP servers.
      </Alert>
    )
  }

  return (
    <Stack gap="lg" maw={960}>
      <Card withBorder p="lg" radius="md">
        <Group gap="sm" mb="md">
          <IconPlug size={22} />
          <Text fw={700} size="lg">
            MCP servers
          </Text>
        </Group>
        <Text size="sm" c="dimmed" mb="md">
          Add MCP servers for this hosted workspace: run a <strong>local process</strong> (stdio) on the machine where
          tools execute, or connect to a remote server over <strong>HTTP(S)</strong> using the MCP SSE transport (GET the
          SSE URL, then POST JSON-RPC to the URL from the <code>endpoint</code> event). Connection tests for SSE run
          from the Owlbert server.
        </Text>
        <Group justify="space-between" mb="sm">
          <Text size="sm">
            <Text span fw={600}>
              {count}
            </Text>{' '}
            of {max} servers
          </Text>
          <Button size="sm" onClick={openCreate} disabled={loading || count >= max}>
            Add server
          </Button>
        </Group>
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Command or URL</Table.Th>
              <Table.Th w={220} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {list.length === 0 && !loading ? (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text size="sm" c="dimmed">
                    No MCP servers yet. Add one to attach it to assistants.
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              list.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td>
                    <Text size="sm">{row.transport_type === 'sse' ? 'HTTPS (SSE)' : 'Local (stdio)'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" lineClamp={2} style={{ wordBreak: 'break-all' }}>
                      {targetLabel(row)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      {row.transport_type === 'sse' ? (
                        <Button
                          size="compact-xs"
                          variant="light"
                          leftSection={<IconPlugConnected size={14} />}
                          loading={testBusy === row.id}
                          disabled={testBusy !== null && testBusy !== row.id}
                          onClick={() => void testSavedSseRow(row)}
                        >
                          Test
                        </Button>
                      ) : null}
                      <Button size="compact-xs" variant="light" leftSection={<IconEdit size={14} />} onClick={() => openEdit(row)}>
                        Edit
                      </Button>
                      <Button
                        size="compact-xs"
                        color="red"
                        variant="light"
                        leftSection={<IconTrash size={14} />}
                        onClick={() => void remove(row)}
                      >
                        Remove
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit MCP server' : 'Add MCP server'} size="lg">
        <Stack gap="md">
          <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} placeholder="My tools" />
          <TextInput
            label="Slug"
            description="Short id for logs (unique per workspace)"
            value={slug}
            onChange={(e) => setSlug(e.currentTarget.value)}
            placeholder="my-tools"
          />
          <div>
            <Text size="sm" fw={500} mb={6}>
              Transport
            </Text>
            <SegmentedControl
              fullWidth
              value={transportKind}
              onChange={(v) => setTransportKind(v as 'stdio' | 'sse')}
              data={[
                { label: 'Local process (stdio)', value: 'stdio' },
                { label: 'Remote HTTP(S) — SSE', value: 'sse' },
              ]}
            />
          </div>
          {transportKind === 'stdio' ? (
            <>
              <TextInput
                label="Command"
                description="Executable path, or paste the full shell command when Arguments is []."
                value={command}
                onChange={(e) => setCommand(e.currentTarget.value)}
                placeholder="/usr/bin/npx"
                required
              />
              <Textarea
                label="Arguments (JSON array of strings)"
                description='Leave as [] if you pasted the full command above. Example: ["-y","tsx","/path/to/mcp.ts","--stdio"]'
                value={argsJson}
                onChange={(e) => setArgsJson(e.currentTarget.value)}
                placeholder='["-y", "@modelcontextprotocol/server-filesystem", "/path"]'
                minRows={3}
                styles={{ input: { fontFamily: 'monospace' } }}
              />
            </>
          ) : (
            <>
              <TextInput
                label="SSE URL"
                description="Full URL of the MCP SSE endpoint (GET)."
                value={sseUrl}
                onChange={(e) => setSseUrl(e.currentTarget.value)}
                placeholder="https://api.example.com/mcp/sse"
                required
              />
              <Textarea
                label="HTTP headers (optional JSON object)"
                description='String values only, e.g. {"Authorization": "Bearer your-token"}'
                value={headersJson}
                onChange={(e) => setHeadersJson(e.currentTarget.value)}
                minRows={4}
                styles={{ input: { fontFamily: 'monospace' } }}
              />
            </>
          )}
          <Group justify="space-between" align="flex-end" wrap="wrap">
            {transportKind === 'sse' ? (
              <Button
                variant="light"
                leftSection={<IconPlugConnected size={16} />}
                loading={testBusy === 'modal'}
                disabled={testBusy !== null && testBusy !== 'modal'}
                onClick={() => void testDraftSse()}
              >
                Test connection
              </Button>
            ) : (
              <span />
            )}
            <Group gap="sm" justify="flex-end">
              <Button variant="default" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => void saveModal()}>Save</Button>
            </Group>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
